import React, { useEffect, useState } from "react";

import { FlexLayout } from "js/Components/FlexLayout";
import { ShowWarningDialog } from "js/react/components/Dialogs/BaseDialog";
import { AiImageStyles, ContextSource, ContextSourceType, ImageStyles } from "common/aiConstants";
import WorkspaceController from "js/controllers/WorkspaceController";
import { ActionPermissionsObject } from "common/interfaces";

import { PaneContent, PaneFooter, PromptSectionContainer } from "../../../components/PaneComponents";
import PromptInput from "../../../components/PromptInput";
import ImageStyleInput from "../../../components/ImageStyleInput";
import AdditionalContextInput from "../../../components/AdditionalContextInput";

import GeneratePresentationController, {
    StartGenerationParams,
    GeneratePresentationState,
} from "../controllers/GeneratePresentationController";

const promptExamples = [
    "Investor pitch deck for a Series A SaaS company in the B2C FinTech space",
    "Board deck for Q1 revenue, growth, and new customers",
    "What is Google's business model?",
    "How the Grinch stole Christmas",
    "A pitch deck for a mobile app that sells train tickets called 'Ticket to Ride'",
    "Presentation on the workplace transformation and the future of work",
];

type PresentationPromptInputProps = {
    handlePromptChange?: (prompt: string) => void,
    handleStartGeneration: (prompt: string) => void,
    startGenerationParams?: StartGenerationParams,
    startGeneratingOnEnter?: boolean,
    contextSource?: ContextSource,
    theme?: any,
}

export function PresentationPromptInput({
    handlePromptChange,
    handleStartGeneration,
    startGenerationParams,
    contextSource = null,
    theme = null,
}: PresentationPromptInputProps) {
    const [prompt, setPrompt] = useState<string>(startGenerationParams?.prompt ?? "");

    const onPromptChange = (newPrompt: string) => {
        setPrompt(newPrompt);
        if (handlePromptChange) {
            handlePromptChange(newPrompt);
        }
    }

    const onEnterKeyPress = (newPrompt: string) => {
        handleStartGeneration(newPrompt);
    };

    let promptPlaceholder = "Describe the topic of your presentation...";
    if (contextSource) {
        promptPlaceholder += " (Note: You can leave this blank to create a presentation just based on the additional context you add)";
    }

    return (
        <PromptInput
            label="Generate a presentation with AI"
            prompt={prompt}
            onPromptChange={onPromptChange}
            promptPlaceholder={promptPlaceholder}
            promptExamples={promptExamples}
            onEnterKeyPress={onEnterKeyPress}
            theme={theme}
            multiline={true}
        />
    );
};

const PromptView = GeneratePresentationController.withState(({
    theme,
    startGenerationParams
}: GeneratePresentationState) => {
    const [prompt, setPrompt] = useState<string>(startGenerationParams?.prompt ?? "");
    const [imageStyle, setImageStyle] = useState<ImageStyles>("theme");
    const [imageStylePrompt, setImageStylePrompt] = useState<string>(theme.get("imageStylePrompt") ?? AiImageStyles["minimalist"].prompt);
    const [contextSource, setContextSource] = useState<ContextSource | null>(null);
    const [language, setLanguage] = useState<string>(startGenerationParams?.language ?? "");

    useEffect(() => {
        if (imageStyle === "theme") {
            setImageStylePrompt(theme.get("imageStylePrompt") ?? AiImageStyles["minimalist"].prompt);
        }
    }, [theme, imageStyle]);

    const handleStartGeneration = (newPrompt?: string) => {
        const params: StartGenerationParams = {
            prompt: newPrompt ?? prompt,
            imageStyle: "stock",
            imageStylePrompt: "",
            files: [],
            contextUrls: [],
            documentText: "",
            language,
        };

        if (WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI].use && WorkspaceController.actionPermissions[ActionPermissionsObject.GENERATIVE_AI_IMAGES].use && WorkspaceController.experiments.ai_gen_images && !WorkspaceController.isEnterprise) {
            params.imageStyle = imageStyle;
            params.imageStylePrompt = imageStylePrompt;
        }

        if (contextSource) {
            switch (contextSource.type) {
                case ContextSourceType.FILE:
                    if (contextSource.file) params.files.push(contextSource.file);
                    break;
                case ContextSourceType.WEBPAGE:
                    if (contextSource.url) params.contextUrls.push(contextSource.url.trim());
                    break;
                case ContextSourceType.TEXT:
                    if (contextSource.text) params.documentText += contextSource.text;
                    break;
            }
        }

        if (params.prompt.trim().length + params.files.length + params.contextUrls.length + params.documentText.trim().length == 0) {
            // @ts-ignore
            return ShowWarningDialog({
                title: "Please enter a prompt or context before generating a presentation",
            });
        }

        GeneratePresentationController.generatePresentationOutline(params);
    };

    const handlePromptChange = (newPrompt: string) => {
        setPrompt(newPrompt);
    };

    const handleContextSourceChange = (contextSource: ContextSource) => {
        setContextSource(contextSource);
    };

    const handleImageStyleChange = (imageStyle: ImageStyles, imageStylePrompt?: string) => {
        setImageStyle(imageStyle);
        setImageStylePrompt(imageStylePrompt);
    };

    const handleThemeChange = (newTheme: any) => {
        GeneratePresentationController.setTheme(newTheme);
    };

    return (
        <>
            <PaneContent>
                <FlexLayout direction="column" width="fill" height="fill" gap={20} scrollable>
                    <PromptSectionContainer>
                        <PresentationPromptInput
                            handlePromptChange={handlePromptChange}
                            handleStartGeneration={handleStartGeneration}
                            startGenerationParams={startGenerationParams}
                            contextSource={contextSource}
                            theme={theme}
                        />
                    </PromptSectionContainer>

                    <PromptSectionContainer>
                        <AdditionalContextInput
                            contextSource={contextSource}
                            onContextSourceChange={handleContextSourceChange}
                            language={language}
                            onLanguageChange={setLanguage}
                        />
                    </PromptSectionContainer>

                    <PromptSectionContainer>
                        <ImageStyleInput
                            theme={theme}
                            onThemeChange={handleThemeChange}
                            imageStyle={imageStyle}
                            imageStylePrompt={imageStylePrompt}
                            onImageStyleChange={handleImageStyleChange}
                        />
                    </PromptSectionContainer>
                </FlexLayout>
            </PaneContent>
            <PaneFooter
                actionButtonText="Generate Presentation"
                onActionButtonClick={() => handleStartGeneration(prompt)}
                actionButtonDisabled={prompt.trim().length === 0 && !contextSource}
            />
        </>
    );
});

export default PromptView;
